import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import Topbar from "@/components/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Upload, Trash, PencilSimple, UserCircle } from "@phosphor-icons/react";
import RuleBuilder from "@/components/RuleBuilder";
import { customersApi } from "@/lib/api";
import { toast } from "sonner";

const defaultRules = [
  { days_after_due: 1, channel: "whatsapp", tone: "gentle" },
  { days_after_due: 3, channel: "whatsapp", tone: "firm" },
  { days_after_due: 7, channel: "voice", tone: "firm" },
  { days_after_due: 14, channel: "both", tone: "final" },
];

export default function CustomersPage() {
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", company: "", language: "en", reminder_rules: defaultRules });

  const load = () => customersApi.list().then(setCustomers);
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm({ name: "", phone: "", email: "", company: "", language: "en", reminder_rules: defaultRules });
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, phone: c.phone, email: c.email || "", company: c.company || "", language: c.language, reminder_rules: c.reminder_rules || defaultRules });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editingId) await customersApi.update(editingId, form);
      else await customersApi.create(form);
      toast.success(editingId ? "Customer updated" : "Customer added");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer and all their invoices?")) return;
    await customersApi.remove(id);
    toast.success("Customer deleted");
    load();
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await customersApi.uploadCsv(file);
      toast.success(`Imported ${r.created} customers`);
      if (r.errors?.length) toast.warning(`${r.errors.length} rows had errors`);
      load();
    } catch { toast.error("Upload failed"); }
    e.target.value = "";
  };

  return (
    <AppLayout>
      <Topbar
        title="Customers"
        subtitle={`${customers.length} customers tracked`}
        actions={
          <div className="flex items-center gap-2">
            <label className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border bg-card hover:bg-muted cursor-pointer text-sm transition-colors" data-testid="csv-upload-label">
              <Upload className="h-4 w-4" weight="duotone" />
              Upload CSV
              <input type="file" accept=".csv" className="hidden" onChange={uploadCsv} data-testid="csv-upload-input" />
            </label>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-customer-button" onClick={openNew} className="bg-primary text-primary-foreground">
                  <Plus className="h-4 w-4 mr-1.5" /> Add Customer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-dialog">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">{editingId ? "Edit Customer" : "New Customer"}</DialogTitle>
                </DialogHeader>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div><Label>Name *</Label><Input data-testid="customer-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>Phone * (E.164)</Label><Input data-testid="customer-phone-input" placeholder="+919876543210" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
                  <div><Label>Email</Label><Input data-testid="customer-email-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Company</Label><Input data-testid="customer-company-input" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} /></div>
                </div>
                <div className="mt-5">
                  <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Escalation Rules</Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-3">
                    AI will automatically trigger these reminders on each day after the invoice due date.
                  </p>
                  <RuleBuilder
                    rules={form.reminder_rules}
                    onChange={(rules) => setForm({ ...form, reminder_rules: rules })}
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="save-customer-button" onClick={submit} className="bg-primary text-primary-foreground">
                    {editingId ? "Update" : "Create"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      <div className="px-6 lg:px-10 py-8">
        <div className="border border-border bg-card rounded-md overflow-hidden" data-testid="customers-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Rules</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.length === 0 && (
                <TableRow><TableCell colSpan={5} className="py-12 text-center text-muted-foreground text-sm">No customers yet. Add one or upload a CSV (columns: name, phone, email, company).</TableCell></TableRow>
              )}
              {customers.map((c, idx) => (
                <TableRow key={c.id} className="stagger-in" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`customer-row-${c.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserCircle className="h-8 w-8 text-muted-foreground" weight="duotone" />
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-muted-foreground">{c.email || "—"}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{c.phone}</TableCell>
                  <TableCell className="text-sm">{c.company || "—"}</TableCell>
                  <TableCell><Badge variant="secondary" className="font-mono">{c.reminder_rules?.length || 0} rules</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}>
                      <PencilSimple className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(c.id)} data-testid={`delete-customer-${c.id}`}>
                      <Trash className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  );
}
